import { Big } from 'big.js'

import { CurrencyService } from '@lemon/core/service/currency.service'
import { AlvMaaritys, Kirjanpitotili, Kirjaus, Kirjausrivi } from '../model/kirjanpito'
import { AlvMaaritysFinder, DocIdProvider } from '../service/alv-lemonator-base.service'

export class KirjanpitoKirjausService {
  constructor(private _currencyService: CurrencyService) {}

  async lisaaYhdistelynRiviKirjaukseen(
    tilinumero: string,
    summaBig: Big,
    alv: AlvMaaritys,
    kirjaus: Kirjaus,
    asiakkaanKaikkiTilitMap: Map<string, Kirjanpitotili>,
    lisaaAlvRivi: boolean,
    alvMaaritysFinder: AlvMaaritysFinder,
    docIdProvider: DocIdProvider,
    projekti?: string,
    confidence?: number,
  ): Promise<void> {
    const summa = this._currencyService.muutaBigDecimalRahaksi(summaBig)
    if (!summa) {
      return
    }
    const tili = asiakkaanKaikkiTilitMap.get(tilinumero)
    const kirjausrivi: Kirjausrivi = {
      av: docIdProvider.annaId(),
      a: null,
      d: summa < 0 ? this._currencyService.roundHalfUp(Math.abs(summa), 2) : null,
      k: summa < 0 ? null : this._currencyService.roundHalfUp(Math.abs(summa), 2),
      t: tili.numero,
    }
    if (projekti) {
      kirjausrivi.pr = projekti
    }
    if (confidence !== undefined) {
      kirjausrivi.c = confidence
    }
    kirjaus.rivit.push(kirjausrivi)
    if (lisaaAlvRivi) {
      kirjausrivi.a = alv?.tunniste || null
      await this.paivitaKirjausrivinAlvRivi(kirjausrivi, kirjaus, false, asiakkaanKaikkiTilitMap, asiakkaanKaikkiTilitMap, alvMaaritysFinder, docIdProvider)
    }
  }

  async paivitaKirjausrivinAlvRivi(
    rivi: Kirjausrivi,
    kirjaus: Kirjaus,
    vanhastaSummasta: boolean,
    kaikkiValittavissaOlevatTilitMap: Map<string, Kirjanpitotili>,
    kaikkiTilitMap: Map<string, Kirjanpitotili>,
    alvMaaritysFinder: AlvMaaritysFinder,
    docIdProvider: DocIdProvider,
  ): Promise<void> {
    const index = kirjaus.rivit.indexOf(rivi)
    const seuraavaRiviOnAlv = kirjaus.rivit.length > index && this.onkoNaytettavariviAlvrivi(kirjaus.rivit[index + 1])
    const sitaSeuraavaRiviOnAlv = seuraavaRiviOnAlv && kirjaus.rivit.length > index + 1 && this.onkoNaytettavariviAlvrivi(kirjaus.rivit[index + 2])

    const alvMaaritys = (await alvMaaritysFinder.annaMaaritys(rivi.a)) || null
    const tili = alvMaaritys ? alvMaaritys.kirjaustili : null
    const alvTili = kaikkiValittavissaOlevatTilitMap.get(tili)
    const alvTiliOnOk = alvTili && alvMaaritys

    if (alvTiliOnOk) {
      // ALV-asetukset ovat kunnossa laskentaa varten.

      // jos prosentti on 0, poistetaan kaikki laskentarivit.
      if (alvMaaritys.prosentti === 0) {
        if (sitaSeuraavaRiviOnAlv && seuraavaRiviOnAlv) {
          kirjaus.rivit.splice(index + 2, 1)
          // naytettavaKirjaus.naytettavatKirjausrivit.splice(index + 2, 1)
        }
        if (seuraavaRiviOnAlv) {
          kirjaus.rivit.splice(index + 1, 1)
          // naytettavaKirjaus.naytettavatKirjausrivit.splice(index + 1, 1)
        }
      } else {
        // Prosentti ei ollut nolla, jatketaan laskentaa
        let ekaAlvRivi: Kirjausrivi = null
        if (seuraavaRiviOnAlv) {
          // Päivitä ALV-rivi
          // Jos ALV-rivin numero pitäisi muuttua
          ekaAlvRivi = kirjaus.rivit[index + 1]
          ekaAlvRivi.a = alvMaaritys.tunniste
          // ekaAlvRivi.alv = alvMaaritys.lyhenne
          ekaAlvRivi.t = alvTili.numero
          // ekaAlvRivi.tilinimi = alvTili.nimi
          // ekaAlvRivi.tilinumero = alvTili.numero
          await this.laskeAlvRiviJaSummatUudelleen(rivi, ekaAlvRivi, vanhastaSummasta, alvMaaritys.kaannetty, alvMaaritysFinder)
        } else {
          // ALV-tili puuttuu, lisää uusi
          ekaAlvRivi = {
            av: docIdProvider.annaId(),
            a: alvMaaritys.tunniste,
            d: null,
            k: null,
            t: alvTili.numero,
          }
          // ekaAlvRivi = this.luoNaytettavaKirjausriviKirjausrivista(alvKirjausrivi)

          kirjaus.rivit.splice(index + 1, 0, ekaAlvRivi)
          // naytettavaKirjaus.naytettavatKirjausrivit.splice(index + 1, 0, ekaAlvRivi)

          await this.laskeAlvRiviJaSummatUudelleen(rivi, ekaAlvRivi, vanhastaSummasta, alvMaaritys.kaannetty, alvMaaritysFinder)
        }

        if (alvMaaritys.kaannetty) {
          const kaannettyAlvTili = kaikkiTilitMap.get('2939')
          if (sitaSeuraavaRiviOnAlv) {
            // Päivitä ALV-rivi
            // Jos ALV-rivin numero pitäisi muuttua
            const alvNaytettavaRivi = kirjaus.rivit[index + 2]
            alvNaytettavaRivi.a = alvMaaritys.tunniste
            alvNaytettavaRivi.t = kaannettyAlvTili.numero
            this.laskeKaanteinenAlvRiviUudelleen(rivi, ekaAlvRivi, alvNaytettavaRivi)
          } else {
            // ALV-tili puuttuu, lisää uusi
            const alvKirjausrivi: Kirjausrivi = {
              av: docIdProvider.annaId(),
              a: alvMaaritys.tunniste,
              d: null,
              k: null,
              t: kaannettyAlvTili.numero,
            }

            kirjaus.rivit.splice(index + 2, 0, alvKirjausrivi)

            this.laskeKaanteinenAlvRiviUudelleen(rivi, ekaAlvRivi, alvKirjausrivi)
          }
        } else if (seuraavaRiviOnAlv && sitaSeuraavaRiviOnAlv) {
          kirjaus.rivit.splice(index + 2, 1)
        }
      }
    } else {
      // ALV-asetukset eivät olleet OK, poistetaan rivit.
      if (sitaSeuraavaRiviOnAlv && seuraavaRiviOnAlv) {
        kirjaus.rivit.splice(index + 2, 1)
      }
      if (seuraavaRiviOnAlv) {
        kirjaus.rivit.splice(index + 1, 1)
      }
    }
  }

  private onkoNaytettavariviAlvrivi(kirjausrivi: Kirjausrivi): boolean {
    return !!(kirjausrivi && kirjausrivi.t && this.onkoAlvTilinumero(kirjausrivi.t))
  }

  private onkoAlvTilinumero(tilinumero: string): boolean {
    return tilinumero === '1763' || tilinumero === '2939' || tilinumero === '2946' // TODO: LOPUT TILINUMEROT
  }

  private asetaKirjausrivinKommentti(kirjausrivi: Kirjausrivi, kommentti: string | null) {
    if (kommentti && kommentti.trim()) {
      kirjausrivi.s = kommentti.trim()
    } else {
      delete kirjausrivi.s
    }
  }

  private async laskeAlvRiviJaSummatUudelleen(kirjausrivi: Kirjausrivi, alvRivi: Kirjausrivi, vanhastaSummasta: boolean, kaannetty: boolean, alvMaaritysFinder: AlvMaaritysFinder): Promise<void> {
    this.asetaKirjausrivinKommentti(alvRivi, kirjausrivi.s)

    // console.log('ALV laskenta', vanhastaSummasta, kaannetty)
    // Uusi summa
    if (kirjausrivi.d) {
      const summasta = vanhastaSummasta ? kirjausrivi.d + alvRivi.d : kirjausrivi.d
      const alvMaaritys = await alvMaaritysFinder.annaMaaritys(kirjausrivi.a)
      const raakaveronOsuus = this.annaRaakaveronOsuus(kaannetty, summasta, alvMaaritys.prosentti)
      // console.log('summasta', summasta, vanhastaSummasta, kaannetty, 'raakaveronOsuus', raakaveronOsuus, 'alvPros', kirjausrivi.alv.prosentti)
      const veronOsuus = this._currencyService.roundHalfUp(raakaveronOsuus, 2)
      alvRivi.d = veronOsuus
      if (kaannetty) {
        kirjausrivi.d = this._currencyService.roundHalfUp(summasta, 2)
      } else {
        kirjausrivi.d = this._currencyService.roundHalfUp(summasta - veronOsuus, 2)
      }
    } else {
      const summasta = vanhastaSummasta ? kirjausrivi.k + alvRivi.k : kirjausrivi.k
      const alvMaaritys = await alvMaaritysFinder.annaMaaritys(kirjausrivi.a)
      const raakaveronOsuus = this.annaRaakaveronOsuus(kaannetty, summasta, alvMaaritys.prosentti)
      // console.log('summasta', summasta, vanhastaSummasta, kaannetty, 'raakaveronOsuus', raakaveronOsuus, 'alvPros', kirjausrivi.alv.prosentti)
      const veronOsuus = this._currencyService.roundHalfUp(raakaveronOsuus, 2)
      alvRivi.k = veronOsuus
      if (kaannetty) {
        kirjausrivi.k = this._currencyService.roundHalfUp(summasta, 2)
      } else {
        kirjausrivi.k = this._currencyService.roundHalfUp(summasta - veronOsuus, 2)
      }
    }
  }

  private annaRaakaveronOsuus(kaannetty: boolean, summasta: number, alvProsentti: number): number {
    if (alvProsentti === 0) {
      return 0
    }
    if (alvProsentti === 100) {
      return summasta
    }
    if (kaannetty) {
      return this._currencyService.annaAlvMaaraAlvittomastaHinnasta(summasta, alvProsentti)
    }
    return this._currencyService.annaAlvMaaraAlvillisestaHinnasta(summasta, alvProsentti)
  }

  private laskeKaanteinenAlvRiviUudelleen(kirjausrivi: Kirjausrivi, ekaAlvRivi: Kirjausrivi, tokaAlvRivi: Kirjausrivi) {
    this.asetaKirjausrivinKommentti(ekaAlvRivi, kirjausrivi.s)
    this.asetaKirjausrivinKommentti(tokaAlvRivi, kirjausrivi.s)

    if (ekaAlvRivi.d) {
      tokaAlvRivi.k = this._currencyService.roundHalfUp(ekaAlvRivi.d, 2)
      tokaAlvRivi.d = null
    } else {
      tokaAlvRivi.d = this._currencyService.roundHalfUp(ekaAlvRivi.k, 2)
      tokaAlvRivi.k = null
    }
  }
}
